// The "Feedback" button, on every screen for signed-in staff (in More on a phone). It opens a drawer
// to report something broken, share an idea, or say what could be quicker. The
// drawer and the screenshot code load only when it's pressed.

import type { FeedbackKind } from "@crm/schemas";
import { ChatCircleText } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useSyncExternalStore } from "react";
import { meQuery } from "../lib/api.ts";
import { secondaryClass } from "./ui.tsx";

const FeedbackDrawer = lazy(() => import("./feedback-drawer.tsx").then((m) => ({ default: m.FeedbackDrawer })));

// One drawer for the whole app: the button, the sidebar link and the crash screen all open it.
let state: { open: boolean; kind: FeedbackKind | null } = { open: false, kind: null };
const listeners = new Set<() => void>();
const set = (next: typeof state) => {
	state = next;
	for (const l of listeners) l();
};
const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => listeners.delete(l);
};

/** Opens the feedback drawer, optionally with "Something's broken" (or another kind) already chosen. */
export const openFeedback = (kind: FeedbackKind | null = null) => set({ open: true, kind });
const closeFeedback = () => set({ open: false, kind: null });

export function FeedbackButton() {
	const { data: me } = useQuery(meQuery);
	const { open, kind } = useSyncExternalStore(subscribe, () => state);
	if (!me) return null;
	const place = `${secondaryClass} fixed right-4 bottom-6 z-30 shadow-lg print:hidden`;
	// On a phone with the tab bar, a floating button covers whatever scrolls under it,
	// so there it's "Feedback" in More instead.
	const wrap = me.activeAgencyId ? "hidden sm:contents" : "contents";
	return (
		<>
			{open ? null : (
				<div className={wrap}>
					<button type="button" data-feedback onClick={() => openFeedback()} className={place}>
						<ChatCircleText size={22} aria-hidden="true" />
						Feedback
					</button>
				</div>
			)}
			{open ? (
				<Suspense
					fallback={
						<button type="button" data-feedback disabled aria-live="polite" className={place}>
							<ChatCircleText size={22} aria-hidden="true" />
							Opening…
						</button>
					}
				>
					<FeedbackDrawer initialKind={kind} onClose={closeFeedback} />
				</Suspense>
			) : null}
		</>
	);
}
